import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import {
  LayoutDashboard, Users, ClipboardList, CreditCard,
  Trophy, Calendar, GitBranch, MessageSquare, LogOut,
  Bell, ChevronDown, ShoppingBag, Video, Settings
} from 'lucide-react';
import logoImg from '../../logo.png';

const NAV = [
  { label: 'Main', items: [
    { to: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard',       roles: ['admin','coach','parent'] },
    { to: '/teams',         icon: Users,           label: 'Teams & Rosters', roles: ['admin','coach','parent'] },
    { to: '/registrations', icon: ClipboardList,   label: 'Registrations',   roles: ['admin','coach'] },
    { to: '/payments',      icon: CreditCard,      label: 'Payments',        roles: ['admin','coach'] },
  ]},
  { label: 'Events', items: [
    { to: '/tournaments', icon: Trophy,      label: 'Tournaments', roles: ['admin','coach','parent'] },
    { to: '/schedule',    icon: Calendar,    label: 'Schedule',    roles: ['admin','coach','parent'] },
    { to: '/brackets',    icon: GitBranch,   label: 'Brackets',    roles: ['admin','coach','parent'] },
  ]},
  { label: 'Club', items: [
    { to: '/highlights', icon: Video,       label: 'Highlights',  roles: ['admin','coach','parent'] },
    { to: '/store',      icon: ShoppingBag, label: 'Team Store',  roles: ['admin','coach','parent'] },
    { to: '/messages',   icon: MessageSquare, label: 'Messages',  roles: ['admin','coach','parent'] },
  ]},
  { label: 'Admin', items: [
    { to: '/admin', icon: Settings, label: 'Site Editor', roles: ['admin'] },
  ]},
];

function UCEShield({ size = 40 }) {
  return <img src={logoImg} alt="UC Elites" width={size} height={size} style={{ objectFit: 'contain' }} />;
}

function NavItem({ to, icon: Icon, label, badge }) {
  return (
    <NavLink to={to}
      style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', gap: 9, padding: '7px 12px',
        borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: isActive ? 700 : 500,
          color: isActive ? '#050505' : 'rgba(255,255,255,0.75)',
        background: isActive
          ? 'var(--gold-main)'
          : 'transparent',
        boxShadow: 'none',
        border: isActive ? '1px solid rgba(255,255,255,0.06)' : '1px solid transparent',
        transition: 'all 0.15s',
        margin: '1px 0',
      })}
      onMouseEnter={e => { if (e.currentTarget.style.background !== 'rgb(198, 163, 90)') { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; } }}
      onMouseLeave={e => { if (!e.currentTarget.classList.contains('active')) { e.currentTarget.style.background = ''; e.currentTarget.style.color = ''; } }}
    >
      <Icon size={15} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{label}</span>
      {badge > 0 && <span style={{ background: '#c62828', color: '#fff', fontSize: 9, fontWeight: 800, padding: '1px 5px', borderRadius: 10, minWidth: 16, textAlign: 'center' }}>{badge}</span>}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { unreadCount } = useApp();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/login'); };

  const visibleNav = NAV.map(section => ({
    ...section,
    items: section.items.filter(item => item.roles.includes(user?.role)),
  })).filter(s => s.items.length > 0);

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside style={{
        width: 'var(--sidebar-w)', flexShrink: 0,
        background: '#050505',
        display: 'flex', flexDirection: 'column', height: '100vh',
        position: 'sticky', top: 0,
        borderRight: '1px solid rgba(255,255,255,0.08)',
      }}>
        {/* Brand */}
        <div style={{ padding: '18px 16px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <UCEShield size={58} />
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28, letterSpacing: '0.06em', lineHeight: 0.95, textTransform: 'uppercase' }}>
                <span style={{ color: 'var(--gold-main)' }}>UC ELITES</span>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.42)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', marginTop: 3, fontFamily: 'var(--font-condensed)' }}>Club Management</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto' }}>
          {visibleNav.map(section => (
            <div key={section.label} style={{ marginBottom: 18 }}>
              <div style={{ color: 'rgba(255,255,255,0.34)', fontSize: 9, fontWeight: 800, letterSpacing: '0.15em', textTransform: 'uppercase', padding: '0 12px', marginBottom: 6, fontFamily: 'var(--font-condensed)' }}>{section.label}</div>
              {section.items.map(item => (
                <NavItem key={item.to} {...item} badge={item.to === '/messages' ? unreadCount : 0} />
              ))}
            </div>
          ))}
        </nav>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '0 12px' }} />

        {/* User */}
        <div style={{ padding: '12px 12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--gold-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#050505', fontSize: 11, fontWeight: 800, flexShrink: 0, fontFamily: 'var(--font-condensed)' }}>{user?.avatar}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#ffffff', fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
            <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, textTransform: 'capitalize', fontFamily: 'var(--font-condensed)', letterSpacing: '0.05em' }}>{user?.role}</div>
            </div>
            <button onClick={handleLogout} title="Logout" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', padding: 4, borderRadius: 4, display: 'flex', transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#ffffff'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}>
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Topbar */}
        <header style={{ height: 56, background: 'rgba(5,5,5,0.96)', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', padding: '0 1.5rem', gap: 12, position: 'sticky', top: 0, zIndex: 100, backdropFilter: 'blur(12px)' }}>
          <div style={{ flex: 1 }} />
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', display: 'flex', padding: 7, borderRadius: 8, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = '#ffffff'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}>
            <Bell size={17} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer', background: 'rgba(255,255,255,0.03)' }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--gold-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#050505', fontSize: 10, fontWeight: 800 }}>{user?.avatar}</div>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#ffffff' }}>{user?.name?.split(' ')[0]}</span>
            <ChevronDown size={12} color="var(--text-muted)" />
          </div>
        </header>

        <div style={{ flex: 1, padding: '1.5rem', overflowY: 'auto', background: 'var(--brand-black)' }}>
          {children}
        </div>
      </main>
    </div>
  );
}
